"use client";
import { themePackStorage } from "@utils/onboarding/themePackStorage";

import { resolveChatHandoff } from "@/src/utils/onboarding/chatHandoffReference";
import { takeOverlapAdChatExit } from "@features/etf-overlap/utils/overlapAdChat";
import { overlapAttributionParams } from "@features/etf-overlap/tracking/overlapAttribution";
import { Track } from "@/src/lib/tracking";

import { LottieLoading } from "@ospweb/sdk-ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { isEmpty } from "lodash-es";
import { useMemoizedFn } from "ahooks";
import { pubsub } from "utils";

import { useSnapshot } from "valtio";
import type { Question } from "../Sender/QuestionItem";
import { useQueryClient } from "@tanstack/react-query";

import { ChatContent } from "./ChatContent";
import { useChatRecommendQuestion } from "@hooks/useChatRecommendQuestion";
import {
  useCreateChat,
  type IChatInfoProps,
  type IOnCreateParams,
} from "@hooks/useCreateChat";
import { useCredits } from "@hooks/useCredits";
import { useHistory } from "@hooks/useHistory";
import { LOCAL_STORAGE_KEYS, QUERY_KEYS } from "@config/constant";
import { ROUTER_PATH } from "@config/router";
import { ChatStore } from "@store/modules/chat";
import { rootStore } from "@store/rootStore";
import { usePathname } from "@/src/hooks/usePathname";
import { useShellRouter } from "@hooks/useShellAwareRouter";
import { ChatSkeleton } from "../Chat/ChatSkeleton";
import { getBridge } from "@/src/lib/bridge";
import { isInNativeChatShellModal } from "@/src/lib/bridge/shellAppNavigation";
import { MessageType } from "@/src/lib/bridge/types";
import { useIsPc } from "@/src/hooks/useIsPc";
import { resolveChatQuestions } from "./resolveChatQuestions";
import { useAdChatPrompts } from "@hooks/useAdChatPrompts";
import {
  isAdChatHandoff,
  resolveAdChatPromptRelated,
} from "@utils/onboarding/adSource";
import {
  getAdSourceCookie,
  resolveAdChatLandingId,
} from "@utils/onboarding/adSourceCookie.client";
import {
  adChatEntryExit,
  adChatEntryView,
  adChatPromptFallback,
} from "@utils/onboarding/adChatTrack";
import { I18nUtil } from "@utils/i18n";
import { useParams } from "next/navigation";
import {
  consumeChatTabPendingEntry,
  persistChatTabPendingEntry,
  type ChatTabPendingEntry,
} from "@utils/chatUtils/chatTabEntry";
import HandoffTranslations from "@translate/components/ChatModal/ThemePackChatHandoff";
import ConnectedTranslations from "@translate/screens/theme-pack/ThemePackConnectedPage";
const HandoffI18n = I18nUtil.createScoped(HandoffTranslations);
const ConnectedI18n = I18nUtil.createScoped(ConnectedTranslations);
import { pubSubKeyMap } from "@utils/pubSubKeyMap";
import { resolveChatModalTargetId } from "./resolveChatModalTargetId";
import { themePackSessionKeys } from "@/src/utils/onboarding/themePackSessionKeys";

export type IChatInfo = IOnCreateParams;

export type ChatModalContentProps = {
  chatInfo?: IChatInfo;
  questions?: Question[];
  /** 页面复用时可隐藏 ChatContent 自带 Header */
  hideHeader?: boolean;
  /** 页面复用时可隐藏 ChatContent 自带 Footer（Sender form） */
  hideFooter?: boolean;
  /**
   * 页面复用时可指定 Footer 渲染形态
   * - sender: 使用既有 Sender（modal 默认）
   * - tab-input: 使用 ChatTabPage 的 Figma 输入框
   * - hidden: 不渲染任何输入区
   */
  footerVariant?: "sender" | "tab-input" | "hidden";
  onClose?: () => void;
  scene?: string;
};

/**
 * ChatModal 内容组件（用于可调用场景，也可用于页面复用）
 */
export const ChatModalContent = ({
  chatInfo,
  questions,
  hideHeader = false,
  hideFooter = false,
  footerVariant,
  onClose,
  scene,
}: ChatModalContentProps) => {
  const shellRouter = useShellRouter();
  const [isPC] = useIsPc();
  const { question, cacheQuestion, sign, tpl, secondOpinion } = chatInfo || {};
  const autoSubmit = chatInfo?.autoSubmit ?? true;
  const autoRenew = chatInfo?.autoRenew ?? false;
  const targetChatId = chatInfo?.targetChatId;
  const deferredThemeHandoff =
    !autoSubmit && Boolean(resolveChatHandoff(question));
  const [contextReady, setContextReady] = useState(true);
  const { questions: recommendQuestions } = useChatRecommendQuestion();
  // 广告态推荐问题取数 key（`AD_CHAT_PROMPT` 的 `related`），两条链路互斥：
  //  - EDG-543：ad_source=CHAT_PROMPT → 取 `landing_id`；无 landing_id 就不发请求；
  //  - EDG-561：其余非 ORGANIC / UNKNOWN / 空的 ad_source → 取 ad_source 原值（后端配置驱动）。
  // 取不到配置 / 超时 / 整组校验不过 → 结果为 default，首屏回到 osp_master 的默认随机口径；
  // 无广告归因的普通用户 key 为 null → 不发请求、结果恒 undefined。
  const routeParams = useParams<{ locale: string }>();
  const adChatLocale = I18nUtil.getBackgroundLanguageKey(
    (routeParams?.locale as string) || "en",
  );
  const adSource = getAdSourceCookie();
  // adchat_* 埋点只属于 CHAT_PROMPT 承接，仍只认 landing_id
  const adChatLandingId = isAdChatHandoff(adSource)
    ? resolveAdChatLandingId()
    : null;
  const adChatResult = useAdChatPrompts(
    adChatLandingId ?? resolveAdChatPromptRelated(adSource),
  );
  const [curChatInfo, setCurChatInfo] = useState<IChatInfoProps | null>({
    key: question,
    cacheQuestion: cacheQuestion,
    sign: sign,
    tpl: tpl,
    secondOpinion,
  });
  const chatContentRef = useRef<{ scrollToBottomOnce?: any }>(null);
  const renewChatRef = useRef(false);
  /** 防止 initChatId effect 在 loading 未翻转前重复触发 onCreateOrg */
  const modalInitCreateStartedRef = useRef(false);
  const [runtimeQuestions, setRuntimeQuestions] = useState<
    Question[] | undefined
  >(undefined);
  const { isLoading: historyLoading, data: history } = useHistory({
    initialPageParam: { limit: 1, offset: 0 },
  });
  const {
    chatInfo: createChatInfo,
    loading,
    hasError,
    onCreateOrg,
  } = useCreateChat({
    isPage: false,
    scene,
  });
  const { loading: isLoading } = useSnapshot(ChatStore);
  const queryClient = useQueryClient();

  useEffect(() => {
    // [App] 监听原生 Chat WebView 的可见性变化，如果处于 loading 状态，则刷新 chat detail
    if (!getBridge().isInApp() || !isInNativeChatShellModal()) {
      return;
    }
    const refreshChatDetailIfLoading = () => {
      if (chatContentRef.current?.scrollToBottomOnce) {
        chatContentRef.current?.scrollToBottomOnce();
      }
      if (document.visibilityState !== "visible" || !isLoading) {
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CHAT_DETAIL],
      });
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CHAT_HISTORY],
      });
    };
    refreshChatDetailIfLoading();
    document.addEventListener("visibilitychange", refreshChatDetailIfLoading);
    window.addEventListener("focus", refreshChatDetailIfLoading);
    return () => {
      document.removeEventListener(
        "visibilitychange",
        refreshChatDetailIfLoading,
      );
      window.removeEventListener("focus", refreshChatDetailIfLoading);
    };
  }, [queryClient, isLoading]);

  useEffect(() => {
    // [App] 监听原生 Chat WebView 的打开事件，获取初始化参数
    if (!getBridge().isInApp() || !isInNativeChatShellModal()) {
      return;
    }
    const unsubscribe = getBridge().on(
      MessageType.SHELL_CHAT_OPEN,
      (payload?: IChatInfo & { questions?: Question[] }) => {
        if (!payload) {
          return;
        }
        if (payload.autoRenew) {
          localStorage.removeItem(LOCAL_STORAGE_KEYS.CHAT_ID);
          renewChatRef.current = true;
        }
        // set 推荐问题
        setRuntimeQuestions(
          payload.questions && payload.questions.length > 0
            ? payload.questions
            : undefined,
        );
        // set 当前会话信息
        if (
          payload.question ||
          payload.sign ||
          payload.tpl ||
          payload.cacheQuestion ||
          payload.secondOpinion
        ) {
          const key =
            payload.sign && payload.question
              ? `${payload.question}_${payload.sign}`
              : payload.question;
          setCurChatInfo((prev) => ({
            ...(prev ?? {}),
            key,
            cacheQuestion: payload.cacheQuestion,
            sign: payload.sign,
            tpl: payload.tpl,
            secondOpinion: payload.secondOpinion,
          }));
        }
        setContextReady(true);
        // 创建新会话
        if (payload.question || payload.shareId) {
          void onCreateOrg({
            ...payload,
            autoSubmit: payload.autoSubmit ?? true,
          });
        }
      },
    );
    return () => {
      unsubscribe();
    };
  }, [onCreateOrg]);

  useEffect(() => {
    // [App] 通知原生当前 Chat WebView 是否处于 loading
    if (!getBridge().isInApp() || !isInNativeChatShellModal()) {
      return;
    }
    getBridge().notifyShellChatLoading(!!isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (autoRenew) {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.CHAT_ID);
      renewChatRef.current = true;
      modalInitCreateStartedRef.current = false;
    }
  }, [autoRenew, chatInfo?.question, chatInfo?.shareId]);

  useEffect(() => {
    const storeChatId = localStorage.getItem(LOCAL_STORAGE_KEYS.CHAT_ID);
    const initChatId = async () => {
      if (targetChatId) {
        localStorage.removeItem("is_submit_chat");
        localStorage.setItem(LOCAL_STORAGE_KEYS.CHAT_ID, targetChatId);
        ChatStore.initChat = deferredThemeHandoff ? question || "" : "";
        ChatStore.initCustomParams = undefined;
        ChatStore.sessionId = targetChatId;
        setCurChatInfo({
          chatId: targetChatId,
          ...(deferredThemeHandoff ? { key: question } : {}),
        });
        return;
      }
      if (
        hasError ||
        (!autoSubmit && !deferredThemeHandoff) ||
        historyLoading
      ) {
        return;
      }
      const latestId = history?.[0]?.id ?? null;
      const targetId = resolveChatModalTargetId({
        autoRenew,
        isLoading: !!isLoading,
        renewChat: renewChatRef.current,
        storeChatId,
        latestId,
      });

      if (targetId) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.CHAT_ID, targetId);
        if (autoSubmit && curChatInfo?.key) {
          localStorage.setItem("is_submit_chat", "1");
        }
        let formatChatInfo = deferredThemeHandoff
          ? curChatInfo
          : !isEmpty(createChatInfo)
            ? createChatInfo
            : curChatInfo;
        if (isEmpty(createChatInfo)) {
          ChatStore.initChat = question || "";
          formatChatInfo = {
            ...(curChatInfo ?? {}),
            key: sign && question ? `${question}_${sign}` : question,
            tpl: tpl,
          };
        }
        setCurChatInfo({
          ...(formatChatInfo ?? {}),
          ...(deferredThemeHandoff ? { key: question } : {}),
          chatId: targetId,
        } as IChatInfoProps);
        return;
      }
      if (
        loading ||
        modalInitCreateStartedRef.current ||
        !(!!chatInfo?.question || !!chatInfo?.shareId)
      ) {
        return;
      }
      modalInitCreateStartedRef.current = true;
      // 将 IChatInfo 转换为 IOnCreateParams，确保所有字段都被传递
      const createParams: IOnCreateParams = {
        question: chatInfo.question,
        cacheQuestion: chatInfo.cacheQuestion,
        sign: chatInfo.sign,
        agentId: chatInfo.agentId,
        tpl: chatInfo.tpl,
        shareId: chatInfo.shareId,
        routerType: chatInfo.routerType,
        localePrefix: chatInfo.localePrefix,
        afterCreate: chatInfo.afterCreate,
        customParams: chatInfo.customParams,
        autoSubmit,
      };
      try {
        const created = await onCreateOrg(createParams);
        renewChatRef.current = false;
        if (deferredThemeHandoff && created?.chatId) {
          setCurChatInfo({ key: question, chatId: created.chatId });
        } else if (createChatInfo) {
          setCurChatInfo(createChatInfo);
        }
      } catch (error) {
        modalInitCreateStartedRef.current = false;
        Track.error(error, {
          tag: "ChatModalContent",
          msg: "initChatId create failed",
        });
      }
    };

    initChatId();
  }, [
    loading,
    createChatInfo,
    hasError,
    autoSubmit,
    deferredThemeHandoff,
    targetChatId,
    autoRenew,
    historyLoading,
    curChatInfo?.chatId,
  ]);

  // 提交一个新对话
  const createNewChat = useMemoizedFn(
    async (
      question: string,
      options?: Omit<IChatInfo, "question"> & {
        customParams?: Record<string, any>;
      },
    ) => {
      ChatStore.initCustomParams = options?.customParams;
      const createParams: IOnCreateParams = {
        question,
        sign: options?.sign,
        cacheQuestion: options?.cacheQuestion,
        agentId: options?.agentId,
        tpl: options?.tpl,
        shareId: options?.shareId,
        routerType: options?.routerType,
        localePrefix: options?.localePrefix,
        afterCreate: options?.afterCreate,
        customParams: options?.customParams,
        // 用户点击发送触发的新会话创建，应自动发送首条消息
        autoSubmit: true,
      };
      await onCreateOrg(createParams);
      renewChatRef.current = false;
      setCurChatInfo({
        key: options?.sign ? `${question}_${options.sign}` : question,
        cacheQuestion: options?.cacheQuestion,
        sign: options?.sign,
        tpl: options?.tpl,
      });
    },
  );

  // 清空对话内容
  const handleNewChat = useMemoizedFn(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.CHAT_ID);
    setCurChatInfo(null);
    setContextReady(false);
    renewChatRef.current = true;
    ChatStore.showRecommendQuestion = true;
    ChatStore.initCustomParams = undefined;
    setTimeout(() => {
      setContextReady(true);
    }, 500);
  });

  // 清空 loading 中的对话，重置对话上下文
  const updateChatContext = (id: string) => {
    // loading 的时候切换 history item，需要重置上下文
    ChatStore.initChat = "";
    ChatStore.initCustomParams = undefined;
    ChatStore.sessionId = id;
    setCurChatInfo({
      chatId: id,
    } as IChatInfoProps);
    renewChatRef.current = true;
    id && localStorage.setItem(LOCAL_STORAGE_KEYS.CHAT_ID, id);
    if (ChatStore.loading) {
      ChatStore.loading = false;
      setContextReady(false);
      setTimeout(() => {
        setContextReady(true);
      }, 500);
    }
  };

  // EDG-543 广告承接埋点：首屏曝光 + 整组回落。非广告会话（landingId 为空）一条都不发。
  useEffect(() => {
    if (!adChatLandingId || !adChatResult) {
      return;
    }
    const attribution = {
      adSource: getAdSourceCookie(),
      landingId: adChatLandingId,
    };
    adChatEntryView({ ...attribution, promptSource: adChatResult.source });
    if (adChatResult.source === "default") {
      adChatPromptFallback({ ...attribution, reason: adChatResult.reason });
    }
  }, [adChatLandingId, adChatResult]);

  const handleClose = useMemoizedFn(() => {
    if (adChatLandingId) {
      adChatEntryExit({
        adSource: getAdSourceCookie(),
        landingId: adChatLandingId,
        messageSent: history.length > 0,
        exitType: "close",
      });
    }
    if (getBridge().isInApp() && isInNativeChatShellModal()) {
      getBridge().dismissShellChat();
      return;
    }
    onClose?.();
    const ownerId = rootStore.user.ownerInfo?.id;
    if (
      ownerId &&
      themePackStorage.getItem(themePackSessionKeys.chatExitBrief(ownerId)) ===
        `chat:${curChatInfo?.chatId}`
    ) {
      themePackStorage.removeItem(themePackSessionKeys.chatExitBrief(ownerId));
      // EDG-561：广告重叠会话的返回，补报 `overlap_chat_exit`（资料包会话取不到，不上报）
      const overlapExit = takeOverlapAdChatExit(curChatInfo?.chatId);
      if (overlapExit) {
        Track.event("overlap_chat_exit", {
          ...overlapAttributionParams(),
          has_result: overlapExit.hasResult,
        });
      }
      shellRouter.replace(ROUTER_PATH.BRIEF);
    }
  });

  const resolvedQuestions = useMemo(() => {
    return resolveChatQuestions({
      isInApp: getBridge().isInApp(),
      runtimeQuestions,
      questions,
      adChatResult,
      recommendQuestions,
      historyLoading,
      historyCount: history.length,
      isPC,
      locale: adChatLocale,
    });
  }, [
    runtimeQuestions,
    questions,
    adChatResult,
    adChatLocale,
    recommendQuestions,
    history.length,
    isPC,
    historyLoading,
  ]);

  useEffect(() => {
    if (history.length === 0 && resolvedQuestions) {
      ChatStore.showRecommendQuestion = true;
    }
  }, [history.length, resolvedQuestions]);

  return contextReady ? (
    <ChatContent
      chatInfo={curChatInfo ?? undefined}
      questions={resolvedQuestions}
      autoSubmit={autoSubmit}
      onClose={handleClose}
      updateChatContext={updateChatContext}
      handleNewChat={handleNewChat}
      createNewChat={createNewChat}
      hideHeader={hideHeader}
      hideFooter={hideFooter}
      footerVariant={footerVariant}
      ref={chatContentRef}
      renewChat={renewChatRef.current}
      scene={scene}
    />
  ) : (
    <div className="flex items-center justify-center h-full">
      <LottieLoading />
    </div>
  );
};

/**
 * ChatTab 内容组件（用于 ChatTab 页面）
 *
 * 目标：与 ChatModalContent 的 modal 初始化逻辑解耦，并复用 ChatContent 的既有推荐问题渲染/交互。
 *
 * 会话初始化优先级：
 * 1. sessionStorage pending entry（PC Plan Goal 等入口；pubsub 可唤醒 re-init）
 * 2. localStorage[CHAT_ID]（续聊）
 * 3. useHistory()[0]（最新会话）
 * 4. 无会话 → 一律停在空态（推荐问），等用户自己输入；无 credits 时额外发一条曝光埋点
 *
 * 📜 曾有第 5 条「无会话且有 credits → 自动创建 onboarding guide 会话」，产品已取消（2026-08-13）。
 */
export const ChatTabContent = () => {
  const { questions } = useChatRecommendQuestion();
  const {
    data: history = [],
    isPending: historyLoading,
    isFetching: historyFetching,
  } = useHistory({
    initialPageParam: { limit: 1, offset: 0 },
  });
  const renewChatRef = useRef(false);
  const { loading: createLoading, onCreateOrg } = useCreateChat({
    isPage: false,
  });
  const { credits, isLoading, isFetching } = useCredits();
  const { isLoading: loginLoading } = useSnapshot(rootStore.login);

  const [resolvedChatId, setResolvedChatId] = useState<string | null>(null);
  const [curChatInfo, setCurChatInfo] = useState<IChatInfoProps | null>(null);
  const [tabContextReady, setTabContextReady] = useState(false);
  /** pubsub 唤醒：keep-alive / 已在 /chat 时再次写入 pending entry */
  const [pendingEntrySignal, setPendingEntrySignal] = useState(0);
  const creatingEmptyChatRef = useRef(false);
  const pendingCreateRef = useRef(false);
  const failedEntryRef = useRef<ChatTabPendingEntry | null>(null);
  const [entryFailed, setEntryFailed] = useState(false);
  const prevPathnameRef = useRef<string | null>(null);
  const pathname = usePathname();

  // 已在 /chat 时，Plan 等入口只写 sessionStorage + emit，靠 signal 触发下方 init 重跑
  useEffect(() => {
    return pubsub.on(pubSubKeyMap.CHAT_TAB_PENDING_ENTRY, () => {
      setPendingEntrySignal((count) => count + 1);
    });
  }, []);

  const handleNewChat = useMemoizedFn(() => {
    failedEntryRef.current = null;
    setEntryFailed(false);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.CHAT_ID);
    setCurChatInfo(null);
    setResolvedChatId(null);
    renewChatRef.current = true;
    creatingEmptyChatRef.current = false;
    ChatStore.showRecommendQuestion = true;
    ChatStore.initCustomParams = undefined;
    setTabContextReady(false);
    setTimeout(() => {
      setTabContextReady(true);
    }, 500);
  });

  const onCreateNewChat = useMemoizedFn(async (input, options) => {
    const createParams: IOnCreateParams = {
      question: input,
      sign: options?.sign,
      cacheQuestion: options?.cacheQuestion,
      agentId: undefined,
      tpl: undefined,
      shareId: undefined,
      routerType: undefined,
      localePrefix: undefined,
      afterCreate: undefined,
      customParams: options?.customParams,
    };
    await onCreateOrg(createParams);
    renewChatRef.current = false;
    // input 为空字符串的“空会话”不应注入 key，否则会影响推荐问题展示
    if (input) {
      setCurChatInfo({
        key: options?.sign ? `${input}_${options.sign}` : input,
        cacheQuestion: options?.cacheQuestion,
        sign: options?.sign,
        tpl: undefined,
      });
    }
  });

  useEffect(() => {
    // 仅删除当前 chat 后，才需要等待 historyFetching 完成，避免所有切换都出现 loading
    ChatStore.showRecommendQuestion = false;
    if (
      historyLoading ||
      createLoading ||
      isLoading ||
      loginLoading ||
      isFetching
    ) {
      return;
    }
    const init = async () => {
      // 优先消费 PC 跨页传入的 pending entry（见 chatTabEntry.ts）
      if (pendingCreateRef.current) {
        return;
      }
      const pendingEntry = consumeChatTabPendingEntry();
      if (!pendingEntry && failedEntryRef.current) {
        return;
      }
      if (pendingEntry) {
        failedEntryRef.current = null;
        setEntryFailed(false);
      }
      if (
        pendingEntry?.targetChatId ||
        pendingEntry?.question ||
        pendingEntry?.shareId ||
        pendingEntry?.secondOpinion
      ) {
        setTabContextReady(false);
        if (pendingEntry.autoRenew) {
          localStorage.removeItem(LOCAL_STORAGE_KEYS.CHAT_ID);
          renewChatRef.current = true;
        }

        if (pendingEntry.targetChatId) {
          localStorage.removeItem("is_submit_chat");
          localStorage.setItem(
            LOCAL_STORAGE_KEYS.CHAT_ID,
            pendingEntry.targetChatId,
          );
          const pendingThemeHandoff =
            pendingEntry.autoSubmit === false &&
            Boolean(resolveChatHandoff(pendingEntry.question));
          ChatStore.initChat = pendingThemeHandoff
            ? pendingEntry.question || ""
            : "";
          ChatStore.initCustomParams = undefined;
          ChatStore.sessionId = pendingEntry.targetChatId;
          setResolvedChatId(pendingEntry.targetChatId);
          setCurChatInfo({
            chatId: pendingEntry.targetChatId,
            ...(pendingThemeHandoff ? { key: pendingEntry.question } : {}),
          });
          renewChatRef.current = false;
          setTabContextReady(true);
          return;
        }

        if (
          pendingEntry.secondOpinion &&
          !pendingEntry.question &&
          !pendingEntry.shareId
        ) {
          const storedChatId = localStorage.getItem(LOCAL_STORAGE_KEYS.CHAT_ID);
          const latestId = history?.[0]?.id;
          const targetId = storedChatId || latestId || null;
          if (targetId) {
            setResolvedChatId(targetId);
            localStorage.setItem(LOCAL_STORAGE_KEYS.CHAT_ID, targetId);
          }
          setCurChatInfo({ secondOpinion: true });
          renewChatRef.current = false;
          setTabContextReady(true);
          return;
        }

        const {
          question,
          cacheQuestion,
          sign,
          tpl,
          shareId,
          autoSubmit = true,
          secondOpinion,
          customParams,
        } = pendingEntry;
        const createParams: IOnCreateParams = {
          question,
          cacheQuestion,
          sign,
          tpl,
          shareId,
          autoSubmit,
          customParams,
        };
        pendingCreateRef.current = true;
        try {
          const { chatId = "" } = (await onCreateOrg(createParams)) || {};
          if (!chatId) {
            failedEntryRef.current = pendingEntry;
            setEntryFailed(true);
            return;
          }
          if (question) {
            setCurChatInfo({
              key: sign && question ? `${question}_${sign}` : question,
              cacheQuestion,
              sign,
              tpl,
              autoSubmit,
              secondOpinion,
            });
            // 与 ChatModalContent modal 分支一致：创建后自动提交首条 question
            if (autoSubmit) {
              localStorage.setItem("is_submit_chat", "1");
            }
          } else if (secondOpinion) {
            setCurChatInfo({ secondOpinion: true });
          }
          if (chatId) {
            setResolvedChatId(chatId);
            localStorage.setItem(LOCAL_STORAGE_KEYS.CHAT_ID, chatId);
          }
          renewChatRef.current = false;
        } catch (error) {
          Track.error(error, {
            tag: "ChatTabContent",
            msg: "pending entry bootstrap failed",
          });
          failedEntryRef.current = pendingEntry;
          setEntryFailed(true);
          return;
        } finally {
          pendingCreateRef.current = false;
        }
        setTabContextReady(true);
        return;
      }

      // 常规初始化：续聊 / 恢复最新 history
      //
      // 📜 这里曾有「零会话兜底」：无 storedChatId、无 history 且 credits>0 时自动建会话并注入隐藏
      // 首问 `[Meet Ed](referenceType=onboarding_guide)\nonboarding guide`。产品已不要这个兜底
      // （2026-08-13）——**空会话就保持空态，等用户自己输入**，不替用户发第一句、不预建会话。
      // 原生 `ChatScreen.kt` 同步删过，别单边加回来。

      const storedChatId = localStorage.getItem(LOCAL_STORAGE_KEYS.CHAT_ID);
      setTabContextReady(false);
      const latestId = history?.[0]?.id;
      const targetId = storedChatId || latestId || null;

      if (targetId) {
        setResolvedChatId(targetId);
        localStorage.setItem(LOCAL_STORAGE_KEYS.CHAT_ID, targetId);
      } else if (isEmpty(history)) {
        // 无会话可续：一律停在空态（推荐问）。无 credits 时额外发一条曝光埋点。
        if (!credits || credits <= 0) {
          Track.view("chat_no_credit_view");
        }
        ChatStore.showRecommendQuestion = true;
      }
      setTabContextReady(true);
    };
    init();
  }, [
    historyLoading,
    historyFetching,
    isLoading,
    loginLoading,
    isFetching,
    history,
    createLoading,
    credits,
    pendingEntrySignal,
    // onCreateOrg 引用不稳定，勿加入 deps，否则 init 会反复触发
  ]);

  // keep-alive 场景：从其他 tab 切回 chat 页面时，若 localStorage CHAT_ID 已空，
  // 直接取内存中缓存的 history[0] 更新会话，无需重新触发 init effect。
  useEffect(() => {
    const prev = prevPathnameRef.current;
    prevPathnameRef.current = pathname;
    if (prev === null) {
      return;
    }
    if (prev === pathname || !pathname.includes(ROUTER_PATH.CHAT)) {
      return;
    }
    if (!localStorage.getItem(LOCAL_STORAGE_KEYS.CHAT_ID)) {
      const latestId = history?.[0]?.id ?? null;
      if (latestId) {
        setResolvedChatId(latestId);
        localStorage.setItem(LOCAL_STORAGE_KEYS.CHAT_ID, latestId);
      }
    }
  }, [pathname, history]);

  if (entryFailed) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center justify-center gap-4 p-6"
      >
        <p>{ConnectedI18n.t("Couldn’t open Ed. Please try again.")}</p>
        <button
          type="button"
          data-openreplay-label="chat_pending_entry_retry"
          onClick={() => {
            const entry = failedEntryRef.current;
            if (!entry) {
              return;
            }
            persistChatTabPendingEntry(entry);
            failedEntryRef.current = null;
            setEntryFailed(false);
            setPendingEntrySignal((count) => count + 1);
          }}
        >
          {HandoffI18n.t("Try again")}
        </button>
      </div>
    );
  }

  return tabContextReady ? (
    <ChatContent
      // 空会话场景不能注入 key，否则 ChatContent 的推荐问题区域不会显示
      chatInfo={{ chatId: resolvedChatId || "", ...curChatInfo }}
      questions={questions.slice(0, 4)}
      createNewChat={onCreateNewChat}
      handleNewChat={handleNewChat}
      headerVariant="tab"
      footerVariant="tab-input"
      autoSubmit={curChatInfo?.autoSubmit ?? true}
      renewChat={renewChatRef.current}
    />
  ) : (
    <div className="px-4">
      <div className="h-14 flex items-center justify-between w-full">
        <div className="w-6 h-6 bg-background_white_overlay5 rounded-[4px] animate-pulse"></div>
        <div className="flex gap-4">
          <div className="w-6 h-6 bg-background_white_overlay5 rounded-[4px] animate-pulse"></div>
          <div className="w-6 h-6 bg-background_white_overlay5 rounded-[4px] animate-pulse"></div>
        </div>
      </div>
      <ChatSkeleton />
    </div>
  );
};
